import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, Check, Download, Eye, X } from 'lucide-react';
import { toast } from 'react-toastify';
import MessageContent from '~/components/MessageContent';
import { messagePlainText } from '~/functions/messageContent';
import { buildSimulatedSentMessages, campaignSimulationEnabled } from './campaignSimulationData';
import { Section, buttonClass, fieldClass, errorText, labelChannel } from './shared';

const CHANNEL_ORDER = ['sms', 'email', 'push'];
const STATUS = {
  accepted: { label: 'Sent', color: '#15803d' },
  failed: { label: 'Not sent', color: '#b91c1c' },
  blocked: { label: 'Not sent', color: '#b91c1c' },
  pending: { label: 'Pending', color: '#f59e0b' },
  processing: { label: 'Pending', color: '#f59e0b' },
  unknown: { label: 'Unknown', color: '#94a3b8' },
};
const ordered = messages => [...messages].sort((left, right) => CHANNEL_ORDER.indexOf(left.channel) - CHANNEL_ORDER.indexOf(right.channel));

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(area);
    if (!copied) throw new Error('Copy is not available in this browser.');
  }
}

const SEPARATOR = "\n\n----------------------------------------\n\n";

function channelEntries(students, channel) {
  return students.flatMap(student => (student.messages || []).filter(message => message.channel === channel).map(message => ({ student, message })));
}

function entryText({ student, message }) {
  const lines = ['Student: ' + student.student_name + (student.registration_number ? ' (' + student.registration_number + ')' : '')];
  if (message.destination) lines.push('To: ' + message.destination);
  if (message.subject) lines.push('Subject: ' + message.subject);
  return lines.join("\n") + "\n\n" + messagePlainText(message.body);
}

export const channelText = (students, channel) => channelEntries(students, channel).map(entryText).join(SEPARATOR);

function downloadText(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function SentMessages({ id, status, request, filial, canPreview = false }) {
  const [students, setStudents] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [simulated, setSimulated] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const confirmed = status === 'confirmed';
  const allowedPreview = canPreview && campaignSimulationEnabled({
    hostname: typeof window === 'undefined' ? '' : window.location.hostname,
    environment: process.env.REACT_APP_ENVIRONMENT || '',
    enabled: process.env.REACT_APP_CAMPAIGN_SIMULATION_ENABLED || '',
  });
  useEffect(() => {
    if (!confirmed || !id) return undefined;
    const abort = new AbortController();
    setError('');
    request('get', '/messaging/campaigns/' + id + '/messages', undefined, abort.signal)
      .then(({ data }) => {
        if (abort.signal.aborted) return;
        if (Number(data.filial_id) !== Number(filial.id)) throw new Error('Location changed. Reopen this campaign.');
        setStudents(data.students || []);
      })
      .catch(failure => { if (!abort.signal.aborted) setError(errorText(failure)); });
    return () => abort.abort();
  }, [id, confirmed, filial.id, request]);

  const list = simulated ? buildSimulatedSentMessages() : students;
  const visible = (list || []).filter(student => (student.student_name + ' ' + (student.registration_number || '')).toLowerCase().includes(search.trim().toLowerCase()));

  return <section aria-label="Sent messages" className="w-full min-w-0">
    <Section title="Sent messages">
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {!confirmed && !simulated && <div className="flex flex-col items-start gap-3 text-sm text-gray-600">
        <p>Messages become available after the campaign is activated and sent.</p>
        {allowedPreview && <button type="button" className={buttonClass} onClick={() => setSimulated(true)}>Preview with sample data (homologation)</button>}
      </div>}
      {simulated && <div role="status" className="flex flex-wrap items-center justify-between gap-2 rounded bg-orange-50 p-3 text-sm"><span><strong>SIMULATION - fictitious data.</strong> Nothing was sent.</span><button type="button" className={buttonClass} onClick={() => setSimulated(false)}>Close simulation</button></div>}
      {(confirmed || simulated) && list === null && !error && <p className="text-sm text-gray-600">Loading messages...</p>}
      {list && <>
        <div className="flex flex-wrap items-end justify-between gap-2">
          <label className="block w-full max-w-md text-xs">Search student<input className={fieldClass} value={search} onChange={event => setSearch(event.target.value)} placeholder="Name or registration number" /></label>
          <button type="button" className={buttonClass} disabled={!list.length} onClick={() => setAllOpen(true)}><Eye size={14} /> View all messages</button>
        </div>
        {allOpen && <AllMessagesModal students={list} onClose={() => setAllOpen(false)} />}
        <ul className="space-y-2">{visible.map(student => <StudentMessages key={student.student_id} student={student} />)}</ul>
        {!visible.length && <p className="text-sm text-gray-600">No students match this search.</p>}
      </>}
    </Section>
  </section>;
}

function StudentMessages({ student }) {
  const messages = ordered(student.messages || []);
  const [open, setOpen] = useState(false);
  return <li className="flex flex-wrap items-center justify-between gap-2 rounded border p-3">
    <div className="min-w-0 flex-1">
      <strong className="text-sm">{student.student_name}</strong>
      <span className="ml-2 text-xs text-gray-600">{student.registration_number}</span>
      <div className="mt-1 flex flex-wrap gap-1">{messages.map(message => <span key={message.channel} className="rounded px-2 py-0.5 text-xs font-semibold text-white" style={{ backgroundColor: (STATUS[message.status] || STATUS.unknown).color }}>{labelChannel(message.channel)}: {(STATUS[message.status] || STATUS.unknown).label}</span>)}</div>
    </div>
    <button type="button" className={buttonClass} onClick={() => setOpen(true)}><Eye size={14} /> View message</button>
    {open && <MessageModal student={student} messages={messages} onClose={() => setOpen(false)} />}
  </li>;
}

function AllMessagesModal({ students, onClose }) {
  const channels = CHANNEL_ORDER.filter(channel => channelEntries(students, channel).length);
  const [channel, setChannel] = useState(channels[0]);
  const [copied, setCopied] = useState(false);
  const entries = channelEntries(students, channel);

  useEffect(() => {
    const close = event => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  async function copyAll() {
    try {
      await copyText(channelText(students, channel));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (failure) {
      toast.error(errorText(failure));
    }
  }

  return createPortal(<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backgroundColor: 'rgba(55, 65, 81, 0.6)' }} onClick={onClose}>
    <div role="dialog" aria-modal="true" aria-label="All messages" style={{ width: '100%', maxWidth: 760, maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 12, padding: 20, borderRadius: 12, backgroundColor: '#fff', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }} onClick={event => event.stopPropagation()}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-bold">All messages</h3>
        <button type="button" aria-label="Close" className="cursor-pointer text-gray-500 hover:text-black" onClick={onClose}><X size={18} /></button>
      </div>
      <div role="tablist" className="flex flex-wrap gap-2">{channels.map(item => <button key={item} type="button" role="tab" aria-selected={item === channel} className={'cursor-pointer rounded border px-3 py-1 text-xs font-semibold ' + (item === channel ? 'border-mila_orange bg-orange-50 text-mila_orange' : 'bg-white')} onClick={() => { setChannel(item); setCopied(false); }}>{labelChannel(item)} ({channelEntries(students, item).length})</button>)}</div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">{entries.map(({ student, message }) => <div key={student.student_id} className="rounded border">
        <div className="space-y-0.5 border-b bg-slate-50 p-2 text-xs"><strong>{student.student_name}</strong>{student.registration_number && <span className="ml-2 text-gray-600">{student.registration_number}</span>}{message.destination && <div className="text-gray-600">To: {message.destination}</div>}{message.subject && <div className="text-gray-600">Subject: {message.subject}</div>}</div>
        <MessageContent body={message.body} className="p-3 text-sm" />
      </div>)}</div>
      <div className="flex justify-end gap-2">
        <button type="button" className={buttonClass} onClick={onClose}>Close</button>
        <button type="button" className={buttonClass} onClick={copyAll}>{copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy all</>}</button>
        <button type="button" className="flex h-8 cursor-pointer items-center gap-1 rounded-md bg-mila_orange px-4 text-xs font-bold text-white" onClick={() => downloadText('messages-' + channel + '.txt', channelText(students, channel))}><Download size={14} /> Download .txt</button>
      </div>
    </div>
  </div>, document.body);
}

function MessageModal({ student, messages, onClose }) {
  const [channel, setChannel] = useState(messages[0]?.channel);
  const [copied, setCopied] = useState(false);
  const current = messages.find(message => message.channel === channel) || messages[0];

  useEffect(() => {
    const close = event => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  async function copy() {
    try {
      await copyText(messagePlainText(current.body));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (failure) {
      toast.error(errorText(failure));
    }
  }

  const channelLabel = labelChannel(current.channel);
  const row = (label, value) => <div style={{ display: 'flex', gap: 8, fontSize: 13 }}><span style={{ width: 70, flexShrink: 0, color: '#6b7280' }}>{label}</span><strong style={{ wordBreak: 'break-word' }}>{value}</strong></div>;
  return createPortal(<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, backgroundColor: 'rgba(55, 65, 81, 0.6)' }} onClick={onClose}>
    <div role="dialog" aria-modal="true" aria-label={'Message for ' + student.student_name} style={{ width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 12, padding: 20, borderRadius: 12, backgroundColor: '#fff', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }} onClick={event => event.stopPropagation()}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-bold">Message sent to the student</h3>
        <button type="button" aria-label="Close" className="cursor-pointer text-gray-500 hover:text-black" onClick={onClose}><X size={18} /></button>
      </div>
      {messages.length > 1 && <div role="tablist" className="flex flex-wrap gap-2">{messages.map(message => <button key={message.channel} type="button" role="tab" aria-selected={message.channel === current.channel} className={'cursor-pointer rounded border px-3 py-1 text-xs font-semibold ' + (message.channel === current.channel ? 'border-mila_orange bg-orange-50 text-mila_orange' : 'bg-white')} onClick={() => { setChannel(message.channel); setCopied(false); }}>{labelChannel(message.channel)}</button>)}</div>}
      <div className="space-y-1 rounded border bg-slate-50 p-3">
        {row('Student', student.student_name + (student.registration_number ? ' (' + student.registration_number + ')' : ''))}
        {row('Channel', channelLabel)}
        {current.destination && row('To', current.destination)}
        {current.subject && row('Subject', current.subject)}
      </div>
      <MessageContent body={current.body} className="min-h-0 flex-1 overflow-y-auto rounded border p-3 text-sm" />
      <div className="flex justify-end gap-2">
        <button type="button" className={buttonClass} onClick={onClose}>Close</button>
        <button type="button" className="flex h-8 cursor-pointer items-center gap-1 rounded-md bg-mila_orange px-4 text-xs font-bold text-white" onClick={copy}>{copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy message</>}</button>
      </div>
    </div>
  </div>, document.body);
}
